import React, { useState, useRef, useEffect } from "react";
import {
  X,
  Loader2,
  Calendar,
  MapPin,
  CheckCircle,
  XCircle,
  CreditCard,
  ShieldCheck,
  ShieldX,
  KeyRound,
  RefreshCw,
  Image as ImageIcon,
  User,
  Eye,
  Edit,
  Camera,
} from "lucide-react";
import { toast } from "react-toastify";
import AadhaarImageUploadStep from "./AadhaarImageUploadStep";
import { normalizeDobForInput, validateAge } from "../../utils/aadhaarImageHelpers";
import ConfirmModal from "../ConfirmModal"; 

const BASE_URL = process.env.REACT_APP_BASE_URL;

const inputCls =
  "w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-transparent transition disabled:bg-gray-50 disabled:cursor-not-allowed";

const SuccessModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
      <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-3">
        <div className="flex flex-col items-center text-center">
          <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
          <h3 className="text-sm font-semibold mb-1">Success!</h3>
          <p className="text-xs text-gray-600 mb-3">{message}</p>
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const ErrorModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1300]">
      <div className="bg-white rounded-lg p-4 max-w-sm w-full mx-3">
        <div className="flex flex-col items-center text-center">
          <XCircle className="h-8 w-8 text-red-500 mb-2" />
          <h3 className="text-sm font-semibold mb-1">Error</h3>
          <p className="text-xs text-gray-600 mb-3">{message}</p>
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const AadhaarDetailsModal = ({
  isOpen,
  onClose,
  workerId,
  onSuccess,
  initialDob = null,
  initialAddress = null,
  initialAadhaar = null,
  isAlreadyVerified = false,
  aadhaarPhoto = null,
  aadharverified,
  aadhaarName = null,
  aadhaarNumber = null,
  aadhaarVerifiedBy = null,
  aadhaarVerifiedOn = null,
  aadhaarDOB = null,
  canEditVerification = false,
}) => {
  const [aadhaar, setAadhaar] = useState(initialAadhaar || aadhaarNumber || "");
  const [aadhaarError, setAadhaarError] = useState("");
  const [step, setStep] = useState(1);
  // null | "otp" | "manual" | "image"
  const [savedNow, setSavedNow] = useState(false);
  const [otpMode, setOtpMode] = useState(null);
  // null | "full" | "partial" — result of the image read
  const [imageReadStatus, setImageReadStatus] = useState(null);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [refId, setRefId] = useState(null);
  const otpRefs = useRef([]);
  const [dob, setDob] = useState(normalizeDobForInput(initialDob || aadhaarDOB));
  const [address, setAddress] = useState(initialAddress || "");
  const [autoFilled, setAutoFilled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [verifiedAadhaarPhoto, setVerifiedAadhaarPhoto] = useState(null);
  const [showDetailsView, setShowDetailsView] = useState(!!aadhaarNumber);
  const [localAadhaarName, setLocalAadhaarName] = useState(aadhaarName || "");
  const [isEditing, setIsEditing] = useState(false);
  // NEW: confirmation before replacing Aadhaar
  const [showEditConfirm, setShowEditConfirm] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
        setShowDetailsView(!!aadhaarNumber); 
        setIsEditing(false);
        setStep(1);
        setOtpMode(null);
        setImageReadStatus(null);
        setOtpSuccess(false);
        setOtpValues(["", "", "", "", "", ""]);
        setAadhaar(aadhaarNumber || "");
        setDob(normalizeDobForInput(initialDob || aadhaarDOB));
        setAddress(initialAddress || "");
        setLocalAadhaarName(aadhaarName || "");
        setShowEditConfirm(false);
      } else {
        setShowDetailsView(false);
        setStep(1);
        setOtpMode(null);
        setImageReadStatus(null);
        setOtpSuccess(false);
        setShowEditConfirm(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getErrorMessage = (data) => {
    if (data.error) return data.error;
    if (data.details?.message) return data.details.message;
    if (data.message) return data.message;
    return "Failed to process request";
  };

  const reset = () => {
    setAadhaar(aadhaarNumber || "");
    setAadhaarError("");
    setStep(1);
    setOtpMode(null);
    setImageReadStatus(null);
    setOtpValues(["", "", "", "", "", ""]);
    setOtpError("");
    setOtpSuccess(false);
    setRefId(null);
    setDob(normalizeDobForInput(initialDob || aadhaarDOB));
    setAddress(initialAddress || "");
    setAutoFilled(false);
    setShowDetailsView(isAlreadyVerified);
    setIsEditing(false);
    setShowEditConfirm(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleAadhaarChange = (e) => {
    const v = e.target.value.replace(/\D/g, "");
    if (v.length <= 12) {
      setAadhaar(v);
      setAadhaarError(v.length > 0 && v.length < 12 ? "Aadhaar must be exactly 12 digits" : "");
    }
  };

  const handleSendOtp = async () => {
    if (!canEditVerification) {
      toast.error("You do not have permission to add Aadhaar details.");
      return;
    }
    if (aadhaar.length !== 12) { setAadhaarError("Aadhaar must be exactly 12 digits"); return; }
    setIsSendingOtp(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await fetch(`${BASE_URL}/lenskart/initiate-aadhaar-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ aadhaarNo: aadhaar, workerId }),
      });
      const data = await res.json();
      if (res.status >= 200 && res.status < 300) {
        if (data.error || data.status === "INVALID" || data.success === false) throw new Error(getErrorMessage(data));
        setRefId(data.refId || null);
        setOtpMode("otp");
        setStep(2);
        toast.success("OTP sent to your registered mobile number!");
      } else if (res.status >= 500 || data.message?.toLowerCase().includes("server")) {
        toast.error("Aadhaar verification unavailable. Please fill details manually.");
        handleManual();
      } else {
        throw new Error(getErrorMessage(data));
      }
    } catch (err) {
      if (err.message === "Failed to fetch" || err.name === "TypeError") {
        toast.error("Network error. Please fill details manually.");
        handleManual();
      } else {
        toast.error(err.message || "Failed to send OTP");
        setAadhaarError(err.message || "Failed to send OTP");
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleManual = () => {
    if (!canEditVerification) {
      toast.error("You do not have permission to add Aadhaar details.");
      return;
    }
    if (aadhaar.length !== 12) { setAadhaarError("Aadhaar must be exactly 12 digits"); return; }
    setOtpMode("manual");
    setImageReadStatus(null);
    setOtpSuccess(false);
    setAutoFilled(false);
    setDob(normalizeDobForInput(initialDob || aadhaarDOB));
    setAddress(initialAddress || "");
    setStep(3);
  };

  // ── Upload via Aadhaar image (works without a typed Aadhaar number) ──
  const handleImageMode = () => {
    if (!canEditVerification) {
      toast.error("You do not have permission to add Aadhaar details.");
      return;
    }
    setAadhaarError("");
    setOtpMode("image");
    setImageReadStatus(null);
    setOtpSuccess(false);
    setAutoFilled(false);
    setStep(2);
  };

  const handleImageComplete = ({
  dob: readDob,
  address: readAddress,
  name: readName,
  aadhaarNo: readAadhaarNo,
  complete,
}) => {
  // Photo read DOB + address fully → already saved/verified by the upload API.
  // Skip the manual "Save Changes" step for both add and edit.
  if (complete) {
  const digits = (readAadhaarNo || "").replace(/\D/g, "");
  if (digits.length === 12) setAadhaar(digits);
  setDob(readDob || "");
  setAddress(readAddress || "");
  if (readName) setLocalAadhaarName(readName);

  setSavedNow(true);
  setIsEditing(false);
  setOtpMode(null);
  setImageReadStatus(null);
  setStep(1);
  setShowDetailsView(true);
  toast.success(isEditing ? "Aadhaar details updated successfully!" : "Aadhaar added successfully!");

  if (onSuccess) onSuccess(readDob || "", readAddress || "", readName || "");
  return;
}

  // Partial read → manual step to fill in the missing details
  setDob(readDob || "");
  setAddress(readAddress || "");
  if (readName) setLocalAadhaarName(readName);
  const digits = (readAadhaarNo || "").replace(/\D/g, "");
  if (aadhaar.length !== 12 && digits.length === 12) {
    setAadhaar(digits);
    setAadhaarError("");
  }
  setImageReadStatus("partial");
  setStep(3);
};

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otpValues];
    next[index] = value.slice(-1);
    setOtpValues(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = [...otpValues];
    pasted.split("").forEach((d, i) => { if (i < 6) next[i] = d; });
    setOtpValues(next);
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async () => {
    if (!canEditVerification) {
      toast.error("You do not have permission to verify Aadhaar details.");
      return;
    }
    const otp = otpValues.join("");
    if (otp.length !== 6) { setOtpError("Please enter the 6-digit OTP"); return; }
    setIsVerifyingOtp(true);
    setOtpError("");
    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await fetch(`${BASE_URL}/lenskart/verify-aadhaar-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp, aadhaarNo: aadhaar, refId, workerId }),
      });
      const data = await res.json();
      if (data.message === "Aadhaar verified successfully") {
        if (data.error || data.status === "INVALID") throw new Error(getErrorMessage(data));
        setOtpSuccess(true);
        if (data.dob) setDob(normalizeDobForInput(data.dob));
        if (data.address) setAddress(data.address);
        if (data.aadhaarPhoto) setVerifiedAadhaarPhoto(data.aadhaarPhoto);
        if (data.aadhaarName) setLocalAadhaarName(data.aadhaarName);
        setAutoFilled(!!(data.dob || data.address));
        toast.success("Aadhaar verified successfully!");
        setStep(3);
      } else if (res.status >= 500 || data.message?.toLowerCase().includes("server")) {
        toast.error("Aadhaar verification unavailable. Please fill details manually.");
        setOtpMode("manual");
        setAutoFilled(false);
        setStep(3);
      } else {
        throw new Error(getErrorMessage(data));
      }
    } catch (err) {
      if (err.message === "Failed to fetch" || err.name === "TypeError") {
        toast.error("Network error. Please fill details manually.");
        setOtpMode("manual");
        setAutoFilled(false);
        setStep(3);
      } else {
        const msg = err.message || "Failed to verify OTP";
        setOtpError(msg);
        toast.error(msg);
      }
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEditVerification) {
      setModalMsg("You do not have permission to update Aadhaar details.");
      setShowError(true);
      return;
    }
    if (aadhaar.length !== 12) {
      setAadhaarError("Aadhaar must be exactly 12 digits");
      setModalMsg("Please enter a valid 12-digit Aadhaar number.");
      setShowError(true);
      return;
    }
    if (!dob) { setModalMsg("Please enter date of birth"); setShowError(true); return; }
    if (!address.trim() || address.trim().length < 5) { setModalMsg("Address must be at least 5 characters."); setShowError(true); return; }
    setIsSubmitting(true);
    try {
      const adminToken = localStorage.getItem("superadmintoken");
      const res = await fetch(`${BASE_URL}/lenskart-admin/update-worker-dob`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          aadhaarNo: aadhaar,
          dob,
          address: address.trim(),
          workerId,
          aadhaarName: localAadhaarName.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setModalMsg("Aadhaar details updated successfully!");
        setShowSuccess(true);
        if (onSuccess) onSuccess(dob, address.trim(), localAadhaarName.trim());
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        throw new Error(data.message || "Failed to update details");
      }
    } catch (err) {
      setModalMsg(err.message || "Failed to update details. Please try again.");
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepLabels =
    otpMode === "manual"
      ? ["Aadhaar", "Details"]
      : otpMode === "image"
      ? ["Aadhaar", "Image", "Details"]
      : ["Aadhaar", "OTP", "Details"];

  const getActualStep = (index) => {
    if (otpMode === "manual") return index === 0 ? 1 : 3;
    return index + 1;
  };

  const getAadhaarPhotoUrl = () => {
    const photoToUse = verifiedAadhaarPhoto || aadhaarPhoto;
    if (!photoToUse) return null;

    if (photoToUse.startsWith("data:image")) return photoToUse;

    if (photoToUse.startsWith("/") || photoToUse.startsWith("9j/")) {
      return `data:image/jpeg;base64,${photoToUse}`;
    }

    if (photoToUse.startsWith("http")) return photoToUse;

    try {
      atob(photoToUse);
      return `data:image/jpeg;base64,${photoToUse}`;
    } catch {
      return photoToUse;
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);     
    setShowDetailsView(true);
    setStep(1);
    setOtpMode(null);
    setImageReadStatus(null);
    setAadhaar(aadhaarNumber || "");
    setAadhaarError("");
    setOtpValues(["", "", "", "", "", ""]);
    setShowEditConfirm(false);
  };

  // NEW: handle confirmation of "Edit Aadhaar Details"
  const handleConfirmEditAadhaar = () => {
    setShowEditConfirm(false);
    setShowDetailsView(false);
    setIsEditing(true);
    setStep(1);
    setOtpMode(null);
    setImageReadStatus(null);
    setOtpSuccess(false);
    setOtpValues(["", "", "", "", "", ""]);
    setAadhaar(aadhaarNumber || "");
    setAadhaarError("");
  };

  const renderDetailsView = () => {
    const photoUrl = getAadhaarPhotoUrl();
    return (
      <div className="space-y-4">
        {/* Verified badge */}
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-700">Aadhaar Verified</p>
            <p className="text-xs text-green-600">Verified on {formatDateDisplay(aadhaarVerifiedOn)}</p>
            <p className="text-xs text-green-600">Verified by {aadhaarVerifiedBy || "N/A"}</p>
          </div>
        </div>

        {/* Aadhaar Photo */}
        {photoUrl && (
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-semibold text-gray-700">Aadhaar Photo</span>
              </div>
              <button
                onClick={() => setShowPhotoModal(true)}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Eye className="h-3 w-3" /> View
              </button>
            </div>
            <div className="flex justify-center">
              <img
                src={photoUrl}
                alt="Aadhaar Photo"
                className="w-32 h-32 rounded-lg object-cover border-2 border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setShowPhotoModal(true)}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 font-medium">Aadhaar Number</p>
            <p className="text-sm font-semibold text-gray-800">XXXX XXXX {aadhaar?.slice(-4) || aadhaarNumber?.slice(-4) || "N/A"}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 font-medium">Aadhaar Name</p>
            <p className="text-sm font-semibold text-gray-800">{localAadhaarName || aadhaarName || "N/A"}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 font-medium">Date of Birth</p>
            <p className="text-sm font-semibold text-gray-800">{dob || aadhaarDOB || initialDob || "N/A"}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-[10px] text-gray-400 font-medium">Verified By</p>
            <p className="text-sm font-semibold text-gray-800">{aadhaarVerifiedBy || "N/A"}</p>
          </div>
        </div>

        {/* Address */}
        <div className="p-2 bg-gray-50 rounded-lg">
          <p className="text-[10px] text-gray-400 font-medium">Address</p>
          <p className="text-sm font-semibold text-gray-800">{address || initialAddress || "N/A"}</p>
        </div>

        {/* Edit button — now opens ConfirmModal first */}
        <button
          onClick={canEditVerification ? () => setShowEditConfirm(true) : undefined}
          disabled={!canEditVerification}
          title={!canEditVerification ? "Not permitted" : undefined}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-md transition ${
            canEditVerification ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          <Edit className="h-4 w-4" />
          Edit Aadhaar Details
        </button>
      </div>
    );
  };

  // ── Blocked state for the ADD flow only ──
  if (!canEditVerification && !isAlreadyVerified) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1200] p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-base font-bold text-gray-800">Aadhaar Verification</h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition">
              <X size={20} />
            </button>
          </div>
          <div className="px-5 py-6 text-center">
            <ShieldX className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-800">Not permitted</p>
            <p className="text-xs text-gray-500 mt-1">
              You do not have permission to add Aadhaar details.
            </p>
            <button
              onClick={handleClose}
              className="mt-4 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1200] p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-2">
              {isAlreadyVerified || otpSuccess ? (
                <ShieldCheck className="h-5 w-5 text-green-600" />
              ) : (
                <ShieldCheck className="h-5 w-5 text-indigo-600" />
              )}
              <h3 className="text-base font-bold text-gray-800">
                {showDetailsView ? "Aadhaar Details" : "Aadhaar Verification"}
              </h3>
              {(isAlreadyVerified || otpSuccess) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-semibold">
                  <CheckCircle className="h-3 w-3" /> Verified
                </span>
              )}
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition">
              <X size={20} />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
            {showDetailsView ? (
              <div className="px-5 py-5">
                {renderDetailsView()}
              </div>
            ) : (
              <>
                {/* Progress steps */}
                <div className="flex items-center px-5 pt-4 gap-2">
                  {stepLabels.map((label, i) => {
                    const actualStep = getActualStep(i);
                    const done = step > actualStep;
                    const active = step === actualStep;
                    return (
                      <React.Fragment key={label}>
                        <div className="flex items-center gap-1.5">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${done ? "bg-green-500 text-white" : active ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-400"}`}>
                            {done ? "✓" : i + 1}
                          </div>
                          <span className={`text-xs font-medium ${active ? "text-indigo-600" : done ? "text-green-600" : "text-gray-400"}`}>{label}</span>
                        </div>
                        {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${done ? "bg-green-400" : "bg-gray-200"}`} />}
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className="px-5 py-5">
                  {/* ── Step 1: Aadhaar number ── */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Aadhaar Number <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <CreditCard className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            inputMode="numeric"
                            value={aadhaar}
                            onChange={handleAadhaarChange}
                            maxLength={12}
                            placeholder="Enter 12-digit Aadhaar number"
                            className={`${inputCls} pl-9 ${aadhaarError ? "border-red-400 focus:ring-red-400" : ""}`}
                          />
                        </div>
                        {aadhaarError && <p className="mt-1 text-xs text-red-500">{aadhaarError}</p>}
                        <p className="mt-1 text-xs text-gray-400">{aadhaar.length}/12 digits</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <button type="button" onClick={handleSendOtp} disabled={aadhaar.length !== 12 || isSendingOtp}
                          className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all border-indigo-200 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed">
                          {isSendingOtp ? <Loader2 className="h-6 w-6 text-indigo-600 animate-spin" /> : <ShieldCheck className="h-6 w-6 text-indigo-600" />}
                          <div className="text-center">
                            <p className="text-xs font-semibold text-indigo-700">Can Receive OTP</p>
                            <p className="text-[10px] text-indigo-500 mt-0.5">Send OTP to Aadhaar</p>
                          </div>
                        </button>
                        <button type="button" onClick={handleManual} disabled={aadhaar.length !== 12}
                          className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-400 disabled:opacity-40 disabled:cursor-not-allowed">
                          <ShieldX className="h-6 w-6 text-orange-500" />
                          <div className="text-center">
                            <p className="text-xs font-semibold text-orange-700">Cannot Receive OTP</p>
                            <p className="text-[10px] text-orange-500 mt-0.5">Enter details manually</p>
                          </div>
                        </button>
                      </div>

                      {/* Third option: upload Aadhaar image (always enabled) */}
                      <button type="button" onClick={handleImageMode}
                        className="w-full flex items-center justify-center gap-3 p-3 rounded-xl border-2 transition-all border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400">
                        <Camera className="h-6 w-6 text-emerald-600" />
                        <div className="text-left">
                          <p className="text-xs font-semibold text-emerald-700">Upload Aadhaar Image</p>
                          <p className="text-[10px] text-emerald-600 mt-0.5">Read DOB &amp; address from Aadhaar photo</p>
                        </div>
                      </button>

                      <p className="text-[11px] text-gray-400 text-center">Enter the 12-digit Aadhaar number to enable OTP and manual options.</p>

                      {isAlreadyVerified && (
                        <button type="button" onClick={cancelEditing} className="w-full text-xs text-gray-500 hover:text-gray-700 transition">
                          Cancel editing
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Step 2 (OTP) ── */}
                  {step === 2 && otpMode === "otp" && (
                    <div className="space-y-5">
                      <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100 flex items-start gap-2">
                        <KeyRound className="h-4 w-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-indigo-700">
                          OTP sent to the mobile number linked with Aadhaar&nbsp;
                          <span className="font-semibold">XXXX XXXX {aadhaar.slice(-4)}</span>.
                          Please enter the 6-digit OTP below.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Enter OTP</label>
                        <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                          {otpValues.map((v, i) => (
                            <input key={i} ref={(el) => (otpRefs.current[i] = el)} type="text" inputMode="numeric" maxLength={1} value={v}
                              onChange={(e) => handleOtpChange(i, e.target.value)} onKeyDown={(e) => handleOtpKeyDown(i, e)}
                              className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all ${otpError ? "border-red-400" : v ? "border-indigo-400 bg-indigo-50" : "border-gray-300"}`}
                            />
                          ))}
                        </div>
                        {otpError && <p className="mt-2 text-xs text-red-500 text-center">{otpError}</p>}
                      </div>
                      <div className="flex gap-3">
                        <button type="button" onClick={() => { setStep(1); setOtpValues(["","","","","",""]); setOtpError(""); }}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition">
                          <RefreshCw size={14} /> Back
                        </button>
                        <button type="button" onClick={handleVerifyOtp} disabled={otpValues.join("").length !== 6 || isVerifyingOtp}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
                          {isVerifyingOtp ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying...</> : "Verify OTP"}
                        </button>
                      </div>
                      <button type="button" onClick={handleSendOtp} disabled={isSendingOtp}
                        className="w-full text-xs text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50 transition">
                        {isSendingOtp ? "Resending..." : "Resend OTP"}
                      </button>
                      <button type="button" onClick={handleManual}
                        className="w-full text-xs text-gray-500 hover:text-orange-600 transition underline underline-offset-2">
                        Didn't receive OTP? Enter details manually instead
                      </button>

                      {isAlreadyVerified && (
                        <button type="button" onClick={cancelEditing} className="w-full text-xs text-gray-500 hover:text-gray-700 transition">
                          Cancel editing
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Step 2 (Image upload) ── */}
                  {step === 2 && otpMode === "image" && (
                    <div className="space-y-4">
                      <AadhaarImageUploadStep
  workerId={workerId}
  isEdit={isEditing}
  onComplete={handleImageComplete}
  onBack={() => { setStep(1); setOtpMode(null); }}
/>
                      <button
                        type="button"
                        onClick={() => {
                          // manual entry from the image flow; the Aadhaar number gets filled in Step 3
                          setOtpMode("image");
                          setImageReadStatus("partial");
                          setOtpSuccess(false);
                          setAutoFilled(false);
                          setStep(3);
                        }}
                        className="w-full text-xs text-gray-500 hover:text-orange-600 transition underline underline-offset-2"
                      >
                        Can't upload image? Enter details manually instead
                      </button>
                      {isAlreadyVerified && (
                        <button type="button" onClick={cancelEditing} className="w-full text-xs text-gray-500 hover:text-gray-700 transition">
                          Cancel editing
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Step 3: Details ── */}
                  {step === 3 && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {otpSuccess && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-green-50 border border-green-200">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-green-700 font-semibold">OTP verified successfully!</p>
                            <p className="text-[11px] text-green-600 mt-0.5">
                              {autoFilled ? "Details auto-filled from Aadhaar — review below." : "Please fill in the details below."}
                            </p>
                          </div>
                        </div>
                      )}

                      {otpMode === "image" && imageReadStatus === "full" && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
                          <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          <p className="text-xs text-emerald-700 font-medium">
                            Details read from Aadhaar image — please review before saving.
                          </p>
                        </div>
                      )}

                      {otpMode === "image" && imageReadStatus === "partial" && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-yellow-50 border border-yellow-200">
                          <ShieldX className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                          <p className="text-xs text-yellow-800 font-medium">
                            Could not read all details from the image — please fill the missing details manually.
                          </p>
                        </div>
                      )}

                      {otpMode === "manual" && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-orange-50 border border-orange-200">
                          <ShieldX className="h-4 w-4 text-orange-500 flex-shrink-0" />
                          <p className="text-xs text-orange-700 font-medium">Manual entry — please fill in the details as per Aadhaar.</p>
                        </div>
                      )}

                      {/* Aadhaar Photo */}
                      {(aadhaarPhoto || verifiedAadhaarPhoto) && (
                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                          <div className="flex items-center gap-2 mb-2">
                            <ImageIcon className="h-4 w-4 text-gray-600" />
                            <span className="text-xs font-semibold text-gray-700">Aadhaar Photo</span>
                            {otpSuccess && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold">
                                <CheckCircle className="h-2.5 w-2.5" /> Verified
                              </span>
                            )}
                          </div>
                          <div className="flex justify-center">
                            <img
                              src={getAadhaarPhotoUrl()}
                              alt="Aadhaar Photo"
                              className="w-32 h-32 rounded-lg object-cover border-2 border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setShowPhotoModal(true)}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-500 text-center mt-2">Click to view full size</p>
                        </div>
                      )}

                      {/* Aadhaar Number — image flow only (OTP/manual already have it from Step 1) */}
                      {otpMode === "image" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Aadhaar Number <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <CreditCard className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              inputMode="numeric"
                              value={aadhaar}
                              onChange={handleAadhaarChange}
                              maxLength={12}
                              disabled={isSubmitting}
                              placeholder="Enter 12-digit Aadhaar number"
                              className={`${inputCls} pl-9 ${aadhaarError ? "border-red-400 focus:ring-red-400" : ""}`}
                            />
                          </div>
                          {aadhaarError && <p className="mt-1 text-xs text-red-500">{aadhaarError}</p>}
                        </div>
                      )}

                      {/* Aadhaar Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Name</label>
                        <div className="relative">
                          <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={localAadhaarName}
                            onChange={(e) => setLocalAadhaarName(e.target.value)}
                            className={`${inputCls} pl-9`}
                            placeholder="Enter name as per Aadhaar"
                          />
                        </div>
                        {otpSuccess && autoFilled && (
                          <p className="mt-1 text-[11px] text-green-600">✓ Name from Aadhaar</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="date"
                            value={dob}
                            onChange={(e) => setDob(e.target.value)}
                            disabled={isSubmitting || (otpSuccess && autoFilled && !!dob)}
                            max={new Date().toISOString().split("T")[0]}
                            className={`${inputCls} pl-9`}
                            required
                          />
                        </div>
                        {otpSuccess && autoFilled && dob && <p className="mt-1 text-[11px] text-green-600">✓ Auto-filled from Aadhaar</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <MapPin className="absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                          <textarea
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            required
                            rows={3}
                            disabled={isSubmitting || (otpSuccess && autoFilled && !!address)}
                            placeholder="Enter full address"
                            className={`${inputCls} pl-9 resize-none`}
                          />
                        </div>
                        {otpSuccess && autoFilled && address && <p className="mt-1 text-[11px] text-green-600">✓ Auto-filled from Aadhaar</p>}
                      </div>

                      <div className="flex gap-3 pt-1">
                        {isAlreadyVerified && (
                          <button
                            type="button"
                            onClick={cancelEditing}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        )}
                        {!isAlreadyVerified && step > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              setStep(step === 3 ? (otpMode === "otp" || otpMode === "image" ? 2 : 1) : step - 1)
                            }
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
                          >
                            Back
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showPhotoModal && (aadhaarPhoto || verifiedAadhaarPhoto) && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1300] p-4">
          <div className="relative max-w-2xl">
            <button onClick={() => setShowPhotoModal(false)} className="absolute -top-4 -right-4 bg-white rounded-full p-2 hover:bg-gray-100 z-10">
              <X size={24} className="text-gray-600" />
            </button>
            <img src={getAadhaarPhotoUrl()} alt="Aadhaar Photo Full Size" className="w-full h-auto rounded-lg max-h-[80vh] object-contain" />
          </div>
        </div>
      )}

      {/* Confirmation before replacing Aadhaar — admin-specific copy */}
      {showEditConfirm && (
        <ConfirmModal
          title="Replace Aadhaar?"
          message="Are you sure you want to delete this worker's current Aadhaar details and add a new Aadhaar?"
          confirmText="Yes, Continue"
          cancelText="Cancel"
          onConfirm={handleConfirmEditAadhaar}
          onCancel={() => setShowEditConfirm(false)}
        />
      )}

      <SuccessModal isOpen={showSuccess} onClose={() => setShowSuccess(false)} message={modalMsg} />
      <ErrorModal isOpen={showError} onClose={() => setShowError(false)} message={modalMsg} />
    </>
  );
};

export default AadhaarDetailsModal;